import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, X, Search, Users, Landmark, Video } from "lucide-react";
import { investorsApi, debtApi, mailApi } from "../lib/api";

const TZ_OPTIONS = [
  ["Eastern Standard Time", "Eastern (ET)"],
  ["Central Standard Time", "Central (CT)"],
  ["Mountain Standard Time", "Mountain (MT)"],
  ["Pacific Standard Time", "Pacific (PT)"],
  ["UTC", "UTC"],
  ["GMT Standard Time", "London (GMT/BST)"],
  ["W. Europe Standard Time", "Central Europe (CET)"],
  ["India Standard Time", "India (IST)"],
];
const IANA_TO_WIN = {
  "America/New_York": "Eastern Standard Time", "America/Detroit": "Eastern Standard Time",
  "America/Chicago": "Central Standard Time", "America/Denver": "Mountain Standard Time",
  "America/Phoenix": "Mountain Standard Time", "America/Los_Angeles": "Pacific Standard Time",
  "Europe/London": "GMT Standard Time", "Europe/Paris": "W. Europe Standard Time",
  "Europe/Berlin": "W. Europe Standard Time", "Asia/Kolkata": "India Standard Time", "UTC": "UTC",
};
const DURATIONS = [15, 30, 45, 60, 90];
const pad = (n) => String(n).padStart(2, "0");
const fmtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
const defaultTz = () => {
  try { return IANA_TO_WIN[Intl.DateTimeFormat().resolvedOptions().timeZone] || "Eastern Standard Time"; }
  catch { return "Eastern Standard Time"; }
};
const tomorrowDate = () => {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const MeetingInviteModal = ({ open, onClose, outlookConnected, mailbox, dealName, prefill }) => {
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(tomorrowDate());
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(30);
  const [tz, setTz] = useState(defaultTz());
  const [useZoom, setUseZoom] = useState(false);
  const [zoomLink, setZoomLink] = useState(localStorage.getItem("xyz_zoom_link") || "");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(prefill?.to || "");
    setName(prefill?.name || "");
    setCc(prefill?.cc || "");
    setSubject(prefill?.subject || (dealName ? `Intro call — ${dealName}` : "Intro call"));
    setQ("");
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open || loaded) return;
    Promise.all([
      investorsApi.list({ limit: 50000 }).catch(() => []),
      debtApi.list().catch(() => []),
    ]).then(([inv, lenders]) => {
      const a = (inv || []).filter((r) => r.email).map((r) => ({ name: r.name || r.firm || r.email, email: r.email, firm: r.firm || "", type: r.type || "Investor", src: "Investor CRM" }));
      const b = (lenders || []).filter((r) => r.email).map((r) => ({ name: r.contact || r.lender || r.email, email: r.email, firm: r.lender || "", type: "Lender", src: "Lender CRM" }));
      setContacts([...a, ...b]);
      setLoaded(true);
    });
  }, [open, loaded]);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return contacts.filter((c) => [c.name, c.email, c.firm].join(" ").toLowerCase().includes(s)).slice(0, 8);
  }, [q, contacts]);

  const pick = (c) => { setTo(c.email); setName(c.name); setQ(`${c.name} · ${c.email}`); };
  const addCc = (c) => setCc((cur) => {
    const have = cur.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (have.includes(c.email)) return cur;
    return have.concat(c.email).join(", ");
  });

  const send = async () => {
    const addr = to.trim();
    if (!addr || !/.+@.+\..+/.test(addr)) return toast.error("Add a valid attendee email address");
    if (!date || !time) return toast.error("Pick a date and time");
    const ccList = cc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const badCc = ccList.find((e) => !/.+@.+\..+/.test(e));
    if (badCc) return toast.error(`Not a valid Cc address: ${badCc}`);
    const zl = zoomLink.trim();
    if (useZoom && !/^https?:\/\//i.test(zl)) return toast.error("Add a valid Zoom link (starting with https://)");
    const start = new Date(`${date}T${time}:00`);
    if (Number.isNaN(start.getTime())) return toast.error("Invalid date/time");
    const end = new Date(start.getTime() + duration * 60000);
    if (useZoom && zl) localStorage.setItem("xyz_zoom_link", zl);
    setBusy(true);
    const tid = toast.loading("Sending meeting invite…");
    try {
      await mailApi.meeting({
        to_email: addr, name, cc: ccList, subject: subject.trim(), note: note.trim(),
        start: fmtLocal(start), end: fmtLocal(end), time_zone: tz,
        zoom_link: useZoom ? zl : "",
      });
      toast.success(`Meeting invite sent to ${addr}${ccList.length ? ` (cc ${ccList.length})` : ""} — they can Accept in Outlook`, { id: tid });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the invite", { id: tid });
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="modal-overlay" data-testid="meeting-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 620, width: "94%" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Send meeting invite
          <button className="btn btn-sm" onClick={onClose} data-testid="meeting-close"><X size={14} /></button>
        </div>
        <div className="modal-sub">
          {outlookConnected
            ? <>Creates the event on <b>{mailbox}</b>'s Outlook calendar and emails the attendee an invite they can Accept or Decline.</>
            : <>Connect your Outlook mailbox first to send calendar invites.</>}
        </div>

        <div className="compose-body">
          <label className="compose-label">Attendee</label>
          <input className="inp" data-testid="meeting-to" placeholder="name@firm.com" value={to}
            onChange={(e) => { setTo(e.target.value); }} />

          <label className="compose-label">Cc <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--text3)" }}>— optional</span></label>
          <input className="inp" data-testid="meeting-cc" placeholder="cc1@firm.com, cc2@firm.com" value={cc}
            onChange={(e) => setCc(e.target.value)} />

          <label className="compose-label">Or pick a contact from your CRM</label>
          <div className="compose-search">
            <Search size={13} />
            <input className="inp" data-testid="meeting-contact-search" placeholder={loaded ? "Search investors & lenders…" : "Loading contacts…"}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {matches.length > 0 && (
            <div className="compose-results" data-testid="meeting-results">
              {matches.map((c) => (
                <div key={`${c.src}-${c.email}`} className="compose-result" data-testid={`meeting-contact-${c.email}`}>
                  {c.type === "Lender" ? <Landmark size={13} /> : <Users size={13} />}
                  <button className="compose-result-main" onClick={() => pick(c)} data-testid={`meeting-pick-${c.email}`}>
                    <b>{c.name}</b>
                    <span>{c.email}{c.firm ? ` · ${c.firm}` : ""}</span>
                  </button>
                  <span className="compose-result-tag">{c.src === "Lender CRM" ? "Lender" : c.type}</span>
                  <button className="compose-cc-add" onClick={() => addCc(c)} title="Add to Cc" data-testid={`meeting-addcc-${c.email}`}>+Cc</button>
                </div>
              ))}
            </div>
          )}

          <label className="compose-label">Title</label>
          <input className="inp" data-testid="meeting-subject" placeholder="Meeting title" value={subject} onChange={(e) => setSubject(e.target.value)} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="compose-label">Date</label>
              <input className="inp" type="date" data-testid="meeting-date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="compose-label">Start time</label>
              <input className="inp" type="time" data-testid="meeting-time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="compose-label">Duration</label>
              <select className="inp" data-testid="meeting-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                {DURATIONS.map((d) => <option key={d} value={d}>{d} minutes</option>)}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label className="compose-label">Time zone</label>
              <select className="inp" data-testid="meeting-tz" value={tz} onChange={(e) => setTz(e.target.value)}>
                {TZ_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="compose-attach-row" style={{ marginTop: 12 }}>
            <label className="ub-chk" style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
              <input type="checkbox" checked={useZoom} onChange={(e) => setUseZoom(e.target.checked)} data-testid="meeting-zoom-toggle" />
              <Video size={14} /> Add Zoom meeting link
            </label>
          </div>
          {useZoom && (
            <input className="inp" data-testid="meeting-zoom-link" placeholder="https://zoom.us/j/..." value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value)} style={{ marginTop: 6 }} />
          )}

          <label className="compose-label">Note <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--text3)" }}>— optional</span></label>
          <textarea className="inp compose-textarea" data-testid="meeting-note" rows={4} placeholder="Add an agenda or message…" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose} data-testid="meeting-cancel">Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={busy || !outlookConnected} data-testid="meeting-send"><CalendarCheck size={13} />{busy ? "Sending…" : "Send invite"}</button>
        </div>
      </div>
    </div>
  );
};
